import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export type MeetingAttendee = {
  user: string;
  userType: string;
};

export type Meeting = {
  _id: string;
  title: string;
  description?: string;
  attendees: MeetingAttendee[];
  scheduledDate: string;
  duration: number;
  meetingPlatform?: string;
  projectId?: string;
  agenda?: string;
  meetingLink?: string;
  isRecurring?: boolean;
  createdBy?: string;
  org?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type MeetingsPayload = {
  title: string;
  description?: string;
  attendees: MeetingAttendee[];
  scheduledDate: string;
  duration: number;
  meetingPlatform?: string;
  projectId?: string;
  agenda?: string;
  meetingLink?: string;
  isRecurring?: boolean;
};

export type MeetingsResponse = {
  success: boolean;
  message: string;
  data: Meeting;
};

export type CancelMeetingPayload = {
  reason: string;
};

export type CancelMeetingVars = {
  meetingId: string;
  payload: CancelMeetingPayload;
};

const cancelMeeting = async (
  vars: CancelMeetingVars
): Promise<MeetingsResponse> => {
  const { meetingId, payload } = vars;
  const { data } = await customAxios.patch<MeetingsResponse>(
    `/meetings/${meetingId}/cancel`,
    payload
  );
  return data;
};

export const useCancelMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation<MeetingsResponse, AxiosError, CancelMeetingVars>({
    mutationFn: cancelMeeting,
    onSuccess: async (data, variables) => {
      showNotification({
        title: "Success",
        message: "Meeting cancelled successfully",
        color: "green",
      });

      await queryClient.invalidateQueries(["meetings"]);
      if (variables?.meetingId) {
        await queryClient.invalidateQueries(["meeting", variables.meetingId]);
      }
    },
    onError: (error) =>
      showNotification({
        title: "Error",
        message:
          (error.response?.data as { message?: string })?.message ??
          "Failed to cancel meeting",
        color: "red",
      }),
  });
};
